<template>
    <div class="o-file-input-container">
        <div 
            class="upload-wrapper mb-3" 
            :class="{ 'has-file': !!filename }"
        >
            <input 
                type="file" 
                class="file-input" 
                :id="id" 
                :name="name" 
                accept=".csv, .xls, .xlsx" 
                @change="onFileChange"
                ref="fileInput"
            >
            
            <!-- File Selected State -->
            <template v-if="filename">
                <div class="file-selected-content">
                    <div class="icon-circle filled">
                        <i class="fas fa-file-excel filled-icon"></i>
                    </div>
                    <div class="file-info text-center">
                        <p class="file-name">{{ filename }}</p>
                        <p class="sub-text fw-medium">Click to change file</p>
                    </div>
                </div>
            </template>
            
            <!-- Placeholder State -->
            <div v-else class="placeholder-content">
                <div class="icon-circle">
                    <i class="fas fa-file-upload upload-icon"></i>
                </div>
                <p class="main-text">Click or drag file here</p>
                <p class="sub-text">CSV, XLS, or XLSX (Max 5MB)</p>
            </div>
        </div>

        <!-- Error Messages -->
        <div v-if="errorFile" class="error-msg-container mb-2">
            <i class="fas fa-exclamation-circle"></i>
            <span>{{ errorFile }}</span>
        </div>
        
        <div v-if="payloadError" class="error-msg-container mb-2">
            <i class="fas fa-sync-alt"></i>
            <span>{{ payloadErrorMessage }}</span>
        </div>

        <div class="progress file d-none" style="height: 48px;">
            <div class="progress-bar" role="progressbar" style="width: 12%"
            aria-valuenow="12" aria-valuemin="0" aria-valuemax="100"></div>
        </div>
    </div>
</template>

<script>
export default {
    emits: ['selectFile'],
    props: {
        name: {
            type: String,
        },
        id: {
            type: String,
        },
        errorPayload: {
            type: String,
            default: ''
        },
    },
    name: "OImportFile",
    data: () => {
        return {
            filename: "",
            errorFile: "",
        }
    },
    computed: {
        validate: function () {
            const validFileSelected = this.filename !== '';
            const hasFileError = this.errorFile !== '';
            return validFileSelected && !hasFileError;
        },
        payloadError: function () {
            return this.errorPayload !== '';
        },
        payloadErrorMessage: function () {
            return this.payloadError ? 'Please refresh browser' : '';
        }
    },
    methods: {
        onFileChange: function (e) {
            const files = e.target.files || e.dataTransfer.files;
            if (!files.length) {
                this.errorFile = "No file selected.";
                this.removeFile(e);
                return;
            }

            const file = files[0];
            const fileType = file.type;
            const validTypes = [
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/csv'
            ];
            
            if (!validTypes.includes(fileType)) {
                this.removeFile(e);
                this.errorFile = "Invalid file type. Only CSV, XLS, and XLSX are allowed.";
                return;
            }

            const bytes = file.size;
            const maxSize = 5 * 1024 * 1024;
            if (bytes > maxSize) {
                this.errorFile = "File size must be less than 5 MB.";
                this.removeFile(e);
                return;
            }
            
            this.errorFile = "";
            this.filename = file.name;
            this.$emit('selectFile', file);
        },
        removeFile: function (e) {
            if (e && e.preventDefault) e.preventDefault();
            this.filename = '';
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        },
    }
}
</script>

<style scoped>
.o-file-input-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
}

.upload-wrapper {
    position: relative;
    width: 100%;
    min-height: 200px;
    border: 2px dashed #cbd5e1;
    background-color: #f8fafc;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    overflow: hidden;
    transition: all 0.2s ease;
}

.upload-wrapper:hover {
    border-color: var(--primary-color, #0d6efd);
    background-color: #eff6ff;
}

.upload-wrapper.has-file {
    border-color: var(--primary-color, #0d6efd);
    background-color: white;
    border-style: solid;
}

.file-input {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 10;
}

/* Placeholder State */
.placeholder-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px;
    pointer-events: none;
}

.icon-circle {
    width: 56px;
    height: 56px;
    background: #f1f5f9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    color: #64748b;
    transition: all 0.2s;
}

.upload-wrapper:hover .icon-circle {
    background: #dbeafe;
    color: var(--primary-color, #0d6efd);
}

.icon-circle.filled {
    background: #dbeafe;
    color: var(--primary-color, #0d6efd);
    margin-bottom: 8px;
}

.upload-icon {
    font-size: 24px;
}

.filled-icon {
    font-size: 24px;
}

.main-text {
    font-size: 15px;
    font-weight: 600;
    color: #334155;
    margin: 0 0 4px 0;
}

.sub-text {
    font-size: 12px;
    color: #94a3b8;
    margin: 0;
}

.file-selected-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px;
    pointer-events: none;
    background-color: #f8fafc;
    width: 100%;
    height: 200px;
    justify-content: center;
}

.file-name {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 4px 0;
    word-break: break-word;
    max-width: 100%;
    padding: 0 16px;
}

.upload-wrapper:hover .file-selected-content {
    background-color: #eff6ff;
}

/* Error Info */
.error-msg-container {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #ef4444;
    font-size: 13px;
    font-weight: 500;
    align-self: flex-start;
}
</style>
